/* eslint-disable import/no-anonymous-default-export */

import { useContext, useCallback, useState } from 'react';
import papaparse from 'papaparse';
import { useHistory } from 'react-router-dom';
import { formatListData, AjaxContext, formatPrevQuestionBankListData, EMPTY_ARRAY } from 'commons-ui';
import _keys from 'lodash/keys';
import _forEach from 'lodash/forEach';
import * as lodash from 'lodash';
import { IAppRequestPayload } from './constants';
import { IQuestion, IQuestionBankCSV } from '../interfaces';

const useService = () => {
	const { postData } = useContext(AjaxContext);
	const [showLoader, setShowLoader] = useState(false);
	const history = useHistory();
	const getQuestionBankList = useCallback(
		async (
			{
				app
			}: {
				app: { displayName?: string; appName: string; isGold: boolean };
			},
			gameName?: string
		) => {
			const { appName } = app;
			try {
				const result = await postData({
					endpoint: '/marketing/dashboard/questionBank/listQuestionBankObjects',
					body: {
						gameName: gameName || 'zcm'
					},
					queryParams: {
						appName
					}
				});
				return {
					rowCount: lodash.get(result, 'data.data.length', 0),
					data: lodash.get(result, 'data.data', EMPTY_ARRAY),
					iAmApprover: lodash.get(result, 'data.iAmApprover', false)
				};
			} catch (error) {
				return { rowCount: 0, data: [], iAmApprover: false };
			}
		},
		[]
	);
	const getQuestionBankConfigs = useCallback(
		async (
			{
				app
			}: {
				app: { displayName?: string; appName: string; isGold: boolean };
			},
			gameName?: string
		) => {
			// const { appName } = app;
			const prevQuestionBankList = await getQuestionBankList({ app }, gameName);
			const reqdList: any = [];
			formatPrevQuestionBankListData(prevQuestionBankList, reqdList);
			const listData = formatListData(reqdList);
			return { rowCount: reqdList.length, data: listData };
		},
		[]
	);

	const requestCreateQuestionBank = useCallback(async (appName: any, data: any) => {
		const formData = new FormData();
		_forEach(_keys(data), (key: string) => {
			formData.append(key, data[key]);
		});
		const csvText = await data.csv.text();
		const questionsData: papaparse.ParseResult<IQuestionBankCSV> = papaparse.parse<IQuestionBankCSV>(csvText, {
			header: true,
			skipEmptyLines: true
		});
		if (lodash.isEmpty(questionsData.data)) {
			throw Error(`CSV is empty`);
		}
		const questionsToUpload: IQuestion[] = questionsData.data.map((question: IQuestionBankCSV) => {
			const answerOptions: string[] = [question.OPTION_1, question.OPTION_2, question.OPTION_3, question.OPTION_4].filter(
				(answer: string) => answer.length !== 0
			);
			if (answerOptions.length < 2) {
				throw Error(`Answer option are less than 2 for question: ${question}`);
			}
			return {
				team1: String(question.TEAM1),
				team2: String(question.TEAM2),
				matchId: String(question.MATCH_ID),
				expireInSecs: Number(question.EXPIRE_IN_SECS),
				text: String(question.TEXT),
				minResponses: Number(question.MIN_RESPONSES),
				maxResponses: Number(question.MAX_RESPONSES),
				randomResponses: Number(question.RANDOM_RESPONSES),
				options: answerOptions,
				pollQText: question.POLL_QUESTION_TEXT
			} as IQuestion;
		});
		const options: {
			endpoint: string;
			body: any;
			customHeaders: any;
			queryParams: any;
			baseUrlFromParams?: string;
		} = {
			endpoint: '/marketing/dashboard/questionBank/saveQuestionBank',
			body: { questions: questionsToUpload },
			customHeaders: { 'Content-Type': 'application/json' },

			queryParams: {
				appName
			}
		};
		return postData(options);
	}, []);

	// const requestCsvDiff = useCallback((appName: any, data: any) => {
	// 	const formData = new FormData();
	// 	_forEach(_keys(data), (key: string) => {
	// 		formData.append(key, data[key]);
	// 	});
	// 	const options: {
	// 		endpoint: string;
	// 		body: any;
	// 		customHeaders: any;
	// 		queryParams: any;
	// 		baseUrlFromParams?: string;
	// 	} = {
	// 		endpoint: '/marketing/dashboard/questionBank/getQuestionBankDiff',
	// 		body: formData,
	// 		customHeaders: { 'Content-Type': 'multipart/form-data' },

	// 		queryParams: {
	// 			appName
	// 		}
	// 	};
	// 	return postData(options);
	// }, []);

	const rollbackQuestionBankFile = useCallback(
		async (
			{
				app
			}: {
				app: { displayName?: string; appName: string; isGold: boolean };
			},
			payload
		) => {
			const { appName } = app;
			const result = await postData({
				endpoint: '/marketing/dashboard/questionBank/rollbackQuestionBankFile',
				body: {
					docId: payload.docId,
					gameName: payload.gameName
				},
				queryParams: {
					appName
				}
			});
			return { success: result?.data?.success, status: result.status };
		},
		[]
	);

	const approveQuestionBankCsvService = useCallback(
		async (
			{
				app
			}: {
				app: IAppRequestPayload;
			},
			payload
		) => {
			const { appName } = app;
			const result = await postData({
				endpoint: '/marketing/dashboard/questionBank/approveQuestionBankCsv',
				body: {
					docId: payload.docId,
					gameName: payload.gameName
				},
				queryParams: {
					appName
				}
			});
			return { success: result?.data?.success, status: result.status };
		},
		EMPTY_ARRAY
	);

	const getQuestionBankCsvStatus = useCallback(
		async (
			{
				app
			}: {
				app: { displayName?: string; appName: string; isGold: boolean };
			},
			payload
		) => {
			const { appName } = app;
			const result = await postData({
				endpoint: '/marketing/dashboard/questionBank/getQuestionBankCsvStatus',
				body: {
					docId: payload.docId,
					environment: payload.environment
				},
				queryParams: {
					appName
				}
			});
			return result;
		},
		[]
	);

	const handleFormCancel = () => {
		history.goBack();
	};

	const requestActivateQuestionBankSpec = useCallback(
		async (app: { displayName?: string; appName: string; isGold: boolean }, specId: any) => {
			const { isGold, appName } = app;
			return postData({
				endpoint: '/marketing/dashboard/questionBank/activateSpec',
				body: { specId, isGold },
				queryParams: { appName }
			});
		},
		[]
	);

	const requestGetLastDeployedQuestionBankSpec = useCallback(
		async (app: { displayName?: string; appName: string; isGold: boolean }) => {
			const { isGold, appName } = app;
			const result = await postData({
				endpoint: '/marketing/dashboard/questionBank/getLastDeployedSpec',
				body: { isGold },
				queryParams: { appName }
			});
			const lastDeployedQuestionBankSpec = result?.data?.questionBankSpec;

			return lastDeployedQuestionBankSpec;
		},
		[]
	);

	return {
		getQuestionBankConfigs,
		requestCreateQuestionBank,
		requestActivateQuestionBankSpec,
		requestGetLastDeployedQuestionBankSpec,
		handleFormCancel,
		showLoader,
		setShowLoader,
		getQuestionBankList,
		rollbackQuestionBankFile,
		getQuestionBankCsvStatus,
		// requestCsvDiff,
		approveQuestionBankCsvService
	};
};

export default useService;
